const { mat4 } = require("pex-math");

function createInstancedPathsCmd(ctx) {
  const defaultModelMatrix = mat4.create();

  const resolution = 4;

  const instanceRoundRound = [
    [0, -0.5, 0],
    [0, -0.5, 1],
    [0, 0.5, 1],
    [0, -0.5, 0],
    [0, 0.5, 1],
    [0, 0.5, 0],
  ];

  // Add the left cap.
  for (let step = 0; step < resolution; step++) {
    const theta0 = Math.PI / 2 + ((step + 0) * Math.PI) / resolution;
    const theta1 = Math.PI / 2 + ((step + 1) * Math.PI) / resolution;
    instanceRoundRound.push([0, 0, 0]);
    instanceRoundRound.push([
      0.5 * Math.cos(theta0),
      0.5 * Math.sin(theta0),
      0,
    ]);
    instanceRoundRound.push([
      0.5 * Math.cos(theta1),
      0.5 * Math.sin(theta1),
      0,
    ]);
  }

  // Add the right cap.
  for (let step = 0; step < resolution; step++) {
    const theta0 = (3 * Math.PI) / 2 + ((step + 0) * Math.PI) / resolution;
    const theta1 = (3 * Math.PI) / 2 + ((step + 1) * Math.PI) / resolution;
    instanceRoundRound.push([0, 0, 1]);
    instanceRoundRound.push([
      0.5 * Math.cos(theta0),
      0.5 * Math.sin(theta0),
      1,
    ]);
    instanceRoundRound.push([
      0.5 * Math.cos(theta1),
      0.5 * Math.sin(theta1),
      1,
    ]);
  }

  const positionBuffer = ctx.vertexBuffer(instanceRoundRound);

  const shaders = {
    vert: /*glsl*/ `
        attribute vec3 aPosition;
        attribute vec3 aPointA;
        attribute vec3 aPointB;
        attribute vec4 aColorA;      
        attribute vec4 aColorB;

        uniform mat4 uProjectionMatrix;
        uniform mat4 uViewMatrix;
        uniform mat4 uModelMatrix;
        uniform float uLineWidth;
        uniform vec2 uResolution;
        uniform float uLineZOffset;      
          
        varying vec4 vColor;

        void main () {  
          vec4 posViewA = uViewMatrix * uModelMatrix * vec4(aPointA, 1.0);
          vec4 posViewB = uViewMatrix * uModelMatrix * vec4(aPointB, 1.0);
          vec4 clip0 = uProjectionMatrix * posViewA;
          vec4 clip1 = uProjectionMatrix * posViewB;
          vec2 screen0 = uResolution * (0.5 * clip0.xy/clip0.w + 0.5);
          vec2 screen1 = uResolution * (0.5 * clip1.xy/clip1.w + 0.5);
          
          vec2 xBasis = normalize(screen1 - screen0);
          vec2 yBasis = vec2(-xBasis.y, xBasis.x);
          float width = uLineWidth;
          float widthA = uLineWidth / -posViewA.z;
          float widthB = uLineWidth / -posViewB.z;
          vec2 pt0 = screen0 + aColorA.a * widthA * (aPosition.x * xBasis + aPosition.y * yBasis);
          vec2 pt1 = screen1 + aColorB.a * widthB * (aPosition.x * xBasis + aPosition.y * yBasis);
          vec2 pt = mix(pt0, pt1, aPosition.z);
          vec4 clip = mix(clip0, clip1, aPosition.z);
          vColor = mix(aColorA, aColorB, aPosition.z);
          
          gl_Position = vec4(clip.w * ((2.0 * pt) / uResolution - 1.0), clip.z + uLineZOffset, clip.w);         
          

          if (length(aPointA) == 0.0 || length(aPointB) == 0.0) {
            gl_Position = vec4(0.0, 0.0, 0.0, 1.0);
          }
        }
        `,
    frag: /*glsl*/ `
        precision highp float;    
        varying vec4 vColor;
        uniform float uOpacity;
          
        void main () {
          gl_FragData[0] = clamp(vColor, vec4(0.0), vec4(1.0));//vec4(0.5, 0.5, 0.5, 0.2);
          // gl_FragData[0] = vec4(0.91);
          gl_FragData[0].a = clamp(uOpacity, 0.0, 1.0);        
          // gl_FragData[0].rgb *= gl_FragData[0].a;

          // gl_FragData[0] = vec4( vec3(0.5),1.0);
        }
        `,
  };

  const geometry = {
    attributes: {
      // just a rectangle
      aPosition: positionBuffer,
      // prev and next point
      aPointA: {
        // buffer: pointsBuffer,
        divisor: 1,
        stride: Float32Array.BYTES_PER_ELEMENT * 3,
      },
      aPointB: {
        // buffer: pointsBuffer,
        divisor: 1,
        stride: Float32Array.BYTES_PER_ELEMENT * 3,
        offset: Float32Array.BYTES_PER_ELEMENT * 3,
      },
      aColorA: {
        // buffer: colorsBuffer,
        divisor: 1,
        stride: Float32Array.BYTES_PER_ELEMENT * 4,
      },
      aColorB: {
        // buffer: colorsBuffer,
        divisor: 1,
        stride: Float32Array.BYTES_PER_ELEMENT * 4,
        offset: Float32Array.BYTES_PER_ELEMENT * 4,
      },
    },
    count: instanceRoundRound.length,
  };

  const blendedPipeline = ctx.pipeline({
    ...shaders,
    primitive: ctx.Primitive.Triangles,
    depthTest: true,
    blend: true,
    blendSrcRGBFactor: ctx.BlendFactor.SrcAlpha,
    blendSrcAlphaFactor: ctx.BlendFactor.One,
    blendDstRGBFactor: ctx.BlendFactor.OneMinusSrcAlpha,
    blendDstAlphaFactor: ctx.BlendFactor.One,
  });

  const cutoutPipeline = ctx.pipeline({
    ...shaders,
    primitive: ctx.Primitive.Triangles,
    depthTest: true,
    blend: true,
    blendSrcRGBFactor: ctx.BlendFactor.Zero,
    blendSrcAlphaFactor: ctx.BlendFactor.Zero,
    blendDstRGBFactor: ctx.BlendFactor.One,
    blendDstAlphaFactor: ctx.BlendFactor.OneMinusSrcAlpha,
  });

  const drawInstancedLinesCmd = {
    pipeline: blendedPipeline,
    ...geometry,
  };

  return function drawInstancedPaths({
    ctx,
    mesh,
    camera,
    opacity = 1,
    cutout = false,
  }) {
    drawInstancedLinesCmd.pipeline = cutout ? cutoutPipeline : blendedPipeline;
    drawInstancedLinesCmd.attributes.aPointA.buffer = mesh.positions.buffer;
    drawInstancedLinesCmd.attributes.aPointB.buffer = mesh.positions.buffer;
    drawInstancedLinesCmd.attributes.aColorA.buffer = mesh.vertexColors.buffer;
    drawInstancedLinesCmd.attributes.aColorB.buffer = mesh.vertexColors.buffer;
    drawInstancedLinesCmd.instances = mesh.positions.data.length - 1;
    ctx.submit(drawInstancedLinesCmd, {
      uniforms: {
        uProjectionMatrix: camera.projectionMatrix,
        uViewMatrix: camera.viewMatrix,
        uModelMatrix: mesh.modelMatrix || defaultModelMatrix, // props.modelMatrix || cachedTransform.modelMatrix,
        uLineWidth: 1.5,
        uResolution: [ctx.gl.drawingBufferWidth, ctx.gl.drawingBufferHeight],
        uLineZOffset: 0,
        uOpacity: opacity,
        // pathEntity?.material?.opacity == undefined
        //   ? 1
        //   : pathEntity?.material?.opacity,
      },
    });
  };
}

export default createInstancedPathsCmd;

// triggerIn.onTrigger = (props) => {
//   const { entities, camera, geometrySystem, transformSystem } = props;

//   const pathEntities = R.filter(R.where({ drawPaths: R.identity }), entities);

//   node.comment = pathEntities.length;

//   pathEntities.forEach((pathEntity) => {
//     const cachedGeometry = geometrySystem.cache[pathEntity.id];
//     const cachedTransform = transformSystem.cache[pathEntity.id];

//     // console.log(cachedTransform)
//     // console.log(cachedGeometry)

//     const drawCmd = drawBlendedCmd;

//     node.comment = pathEntity?.material?.opacity;

//     ctx.submit(drawCmd, {
//       uniforms: {
//         uProjectionMatrix: camera.projectionMatrix,
//         uViewMatrix: camera.viewMatrix,
//         uModelMatrix: props.modelMatrix || cachedTransform.modelMatrix,
//         uLineWidth: withIn.value,
//         uResolution: [ctx.gl.drawingBufferWidth, ctx.gl.drawingBufferHeight],
//         uLineZOffset: 0,
//         uOpacity:
//           pathEntity?.material?.opacity == undefined
//             ? 1
//             : pathEntity?.material?.opacity,
//       },
//       attributes: {
//         // just a rectangle
//         aPosition: positionBuffer,
//         // prev and next point
//         aPointA: {
//           buffer: cachedGeometry.attributes.aPosition.buffer,
//           divisor: 1,
//           stride: Float32Array.BYTES_PER_ELEMENT * 3,
//         },
//         aPointB: {
//           buffer: cachedGeometry.attributes.aPosition.buffer,
//           divisor: 1,
//           stride: Float32Array.BYTES_PER_ELEMENT * 3,
//           offset: Float32Array.BYTES_PER_ELEMENT * 3,
//         },
//         aColorA: {
//           buffer: cachedGeometry.attributes.aVertexColor.buffer,
//           divisor: 1,
//           stride: Float32Array.BYTES_PER_ELEMENT * 4,
//         },
//         aColorB: {
//           buffer: cachedGeometry.attributes.aVertexColor.buffer,
//           divisor: 1,
//           stride: Float32Array.BYTES_PER_ELEMENT * 4,
//           offset: Float32Array.BYTES_PER_ELEMENT * 4,
//         },
//       },
//       instances: cachedGeometry.geometry.positions[0].length
//         ? cachedGeometry.geometry.positions.length - 1
//         : cachedGeometry.geometry.positions.length / 3 - 1,
//     });
//   });
//   triggerOut.trigger(props);
// };
// };
